import { type PropsWithChildren, useMemo } from 'react';
import { Navigate } from 'react-router-dom';

import { useGetOnboardingStepsQuery } from '@/entities/onboarding';
import { useViewerData, useViewerDataStatus } from '@/entities/viewer';

import { getOnboardingPath } from '@/shared/constants/router';
import { calculateOnboardingCompletion } from '@/shared/lib/onboarding';
import { PageLoader } from '@/shared/ui/page-loader';

interface OnboardingGuardProps extends PropsWithChildren {}

/**
 * Guard component that redirects to onboarding if user hasn't completed it
 * Waits for both viewer data and onboarding steps to be loaded before making decision
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
    const viewerData = useViewerData();
    const viewerStatus = useViewerDataStatus();

    // Get onboarding steps with current locale
    const { data: onboardingSteps, isSuccess: isOnboardingStepsLoaded } =
        useGetOnboardingStepsQuery({
            locale: 'en', // TODO: Get from i18n context if needed
        });

    // Check if we have all required data
    const isViewerDataReady = viewerStatus === 'success' && viewerData;
    const areOnboardingStepsReady = isOnboardingStepsLoaded && onboardingSteps;
    const allDataReady = isViewerDataReady && areOnboardingStepsReady;

    // Calculate onboarding completion when both data sources are available
    const isOnboardingCompleted = useMemo(() => {
        if (!allDataReady || !viewerData || !onboardingSteps) {
            return null; // Still waiting for data
        }

        // If user has an active subscription, consider onboarding complete
        // This handles the case where Stripe redirects back but the
        // "subscription" step was never marked as done in onboarding_steps_done
        if (viewerData.subscription?.is_active) {
            return true;
        }

        // Additional safety checks
        const stepsDone = viewerData.onboarding_steps_done;

        if (!stepsDone || !Array.isArray(stepsDone)) {
            return false; // No valid steps done data
        }

        if (!Array.isArray(onboardingSteps)) {
            return false; // No valid onboarding steps data
        }

        return calculateOnboardingCompletion(stepsDone, onboardingSteps);
    }, [allDataReady, viewerData, onboardingSteps]);

    // Show loader while waiting for data
    if (!allDataReady) {
        return <PageLoader />;
    }

    // If user hasn't completed onboarding, redirect to onboarding
    if (!isOnboardingCompleted) {
        return (
            <Navigate
                replace
                to={getOnboardingPath()}
            />
        );
    }

    // User has completed onboarding, render children
    return children;
}
